import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  AgentProfileId,
  AppointmentDto,
  AppointmentSlotStatus,
  DiscussionDto,
  MessageDto,
  OpeningHoursDto,
  PropertyApplicationCreationDto,
  PropertyApplicationDto,
  PropertyDto,
  UserApplications,
} from '@workspace/data-types';
import { AlertSnackBarService, DiscussionStateService } from '@workspace/ng-aura';
import { ChatPendingAction } from '@workspace/ng-core-features';
import { ApplicationStore, NotificationStateService, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, filter, finalize, of, switchMap, tap } from 'rxjs';
import {
  PropertyApplicationDialogComponent,
  PropertyApplicationDialogData,
} from '../property/components/property-application/dialog/property-application-dialog.component';
import {
  PropertyVisitInvitationDialogComponent,
  PropertyVisitInvitationDialogData,
  VisitInvitationResult,
} from '../property/components/property-application/visit-dialog/property-visit-invitation-dialog.component';
import { DiscussionChatComponent } from './components/discussion-chat/discussion-chat.component';
import { DiscussionPropertySidebarComponent } from './components/discussion-property-sidebar/discussion-property-sidebar.component';
import { DiscussionSidebarComponent } from './components/discussion-sidebar/discussion-sidebar.component';
import {
  DiscussionVisitDialogComponent,
  DiscussionVisitDialogData,
  DiscussionVisitDialogResult,
} from './components/discussion-visit-dialog';

@Component({
  selector: 'etxe-discussion',
  imports: [
    CommonModule,
    DiscussionSidebarComponent,
    DiscussionChatComponent,
    DiscussionPropertySidebarComponent,
  ],
  host: {
    class: 'block w-full h-full',
  },
  templateUrl: './discussion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly userStore = inject(UserStore);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly discussionState = inject(DiscussionStateService);
  private readonly alertSnackbar = inject(AlertSnackBarService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('chatFeed') chatFeed?: DiscussionChatComponent;

  discussionId = input<string | null>(null);
  discussionSelected = output<string>();

  readonly discussions = signal<DiscussionDto[]>([]);
  readonly activeDiscussion = signal<DiscussionDto | null>(null);
  readonly currentTab = signal<'all' | 'unread' | 'archived'>('all');
  readonly messages = signal<MessageDto[]>([]);
  readonly propertyContext = signal<PropertyDto | null>(null);
  readonly currentAppointment = signal<AppointmentDto | null>(null);
  readonly currentApplication = signal<PropertyApplicationDto | null>(null);

  readonly isOwner = computed(() => this.applicationStore.name() === UserApplications.OWNER);

  readonly activePinnedMessage = computed<MessageDto | null>(() => {
    const active = this.activeDiscussion();
    const userId = this.userStore.current()?._id;
    if (!active || !userId) return null;
    const me = active.participants.find((p) => p.participant.toString() === userId.toString());
    const pinned = me?.pinnedMessage;
    if (pinned && typeof pinned === 'object' && 'text' in pinned) {
      return pinned as unknown as MessageDto;
    }
    return null;
  });

  readonly timeFormat = computed(() => {
    const lang = this.userStore.current()?.settings?.internationalization?.language;
    return lang === 'fr-FR' ? 'HH:mm' : 'h:mm a';
  });

  readonly decoratedMessages = computed(() => {
    const list = this.messages();
    const userId = this.userStore.current()?._id;
    if (!userId) return [];

    const lang = this.userStore.current()?.settings?.internationalization?.language || 'fr-FR';
    const activeDisc = this.activeDiscussion();

    // Compute unread count for other participants
    const otherParticipants =
      activeDisc?.participants?.filter((p) => p.participant.toString() !== userId.toString()) || [];
    const otherUnreadCount = otherParticipants.reduce((max, p) => Math.max(max, p.unreadCount || 0), 0);

    // Let's determine which messages are read by other participants
    // We scan from newest to oldest
    let remainingUnread = otherUnreadCount;
    const readStatuses = new Array<boolean>(list.length);

    for (let i = list.length - 1; i >= 0; i--) {
      const msg = list[i];
      const isMe = msg.sender.toString() === userId.toString();
      if (isMe) {
        if (remainingUnread > 0) {
          readStatuses[i] = false;
          remainingUnread--;
        } else {
          readStatuses[i] = true;
        }
      } else {
        readStatuses[i] = false; // B's messages don't show read check marks to B
      }
    }

    return list.map((msg, idx) => {
      const isMe = msg.sender.toString() === userId.toString();

      // isLastInGroup
      let isLastInGroup = true;
      const nextMsg = list[idx + 1];
      if (nextMsg && msg.sender.toString() === nextMsg.sender.toString()) {
        const currentDiff = new Date(msg.createdAt).getTime();
        const nextDiff = new Date(nextMsg.createdAt).getTime();
        isLastInGroup = Math.floor(currentDiff / 60000) !== Math.floor(nextDiff / 60000);
      }

      // isSameGroupAsPrevious
      let isSameGroupAsPrevious = false;
      if (idx > 0) {
        const prevMsg = list[idx - 1];
        if (msg.sender.toString() === prevMsg.sender.toString()) {
          const currentDiff = new Date(msg.createdAt).getTime();
          const prevDiff = new Date(prevMsg.createdAt).getTime();
          isSameGroupAsPrevious = Math.floor(currentDiff / 60000) === Math.floor(prevDiff / 60000);
        }
      }

      // dateHeader calculation
      let dateHeader: string | null = null;
      const currentMsgDate = new Date(msg.createdAt);
      if (idx === 0) {
        dateHeader = getFormattedDateHeader(currentMsgDate, lang);
      } else {
        const prevMsgDate = new Date(list[idx - 1].createdAt);
        if (
          currentMsgDate.getFullYear() !== prevMsgDate.getFullYear() ||
          currentMsgDate.getMonth() !== prevMsgDate.getMonth() ||
          currentMsgDate.getDate() !== prevMsgDate.getDate()
        ) {
          dateHeader = getFormattedDateHeader(currentMsgDate, lang);
        }
      }

      const msgObj = msg as unknown as Record<string, unknown>;
      const sending = msgObj['status'] === 'sending';
      const failed = msgObj['status'] === 'failed';
      const errorMessage = msgObj['errorMessage'] as string | undefined;
      const isReadByAll = !sending && !failed && isMe && readStatuses[idx];

      return {
        ...msg,
        isMe,
        isLastInGroup,
        isSameGroupAsPrevious,
        dateHeader,
        sending,
        failed,
        isReadByAll,
        errorMessage,
      };
    });
  });

  readonly isLoadingDiscussions = signal(true);
  readonly isLoadingMessages = signal(false);
  readonly isLoadingMoreDiscussions = signal(false);
  readonly hasMoreDiscussions = signal(true);
  private readonly discussionsLimit = 15;
  private discussionsSkip = 0;
  private lastLoadedDiscussionId: string | null = null;

  constructor() {
    const notificationState = inject(NotificationStateService);
    const destroyRef = inject(DestroyRef);

    // Track active discussion in state service to optimize notification refreshes
    effect(() => {
      const active = this.activeDiscussion();
      this.discussionState.activeDiscussionId.set(active?._id?.toString() || null);
    });

    // Load messages and property context when the active discussion ID changes
    effect(() => {
      const active = this.activeDiscussion();
      const activeId = active?._id?.toString() || null;
      if (activeId && activeId !== this.lastLoadedDiscussionId) {
        this.lastLoadedDiscussionId = activeId;
        untracked(() => {
          this.loadMessages(activeId);
          this.loadAppointment(activeId);
          this.loadApplication(activeId);

          if (active?.context?.property) {
            const appName = this.applicationStore.name();
            const getProperty$ =
              appName === UserApplications.OWNER
                ? this.api.business().etxe().properties().owner().get(active.context.property)
                : this.api.business().etxe().properties().agent().get(active.context.property);

            getProperty$
              .pipe(
                tap((prop) => this.propertyContext.set(prop)),
                catchError(() => {
                  this.propertyContext.set(null);
                  return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe();
          } else {
            this.propertyContext.set(null);
          }
        });
      } else if (!activeId) {
        this.lastLoadedDiscussionId = null;
        untracked(() => {
          this.messages.set([]);
          this.propertyContext.set(null);
          this.currentAppointment.set(null);
          this.currentApplication.set(null);
        });
      }
    });

    // Synchronize the unread count in state service with local discussions list changes
    effect(() => {
      const list = this.discussions();
      this.discussionState.updateUnreadCountFromList(list);
    });

    destroyRef.onDestroy(() => {
      this.discussionState.activeDiscussionId.set(null);
    });

    notificationState.chatMessageReceived
      .pipe(
        tap((metadata) => this.handleIncomingMessageNotification(metadata)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    effect(() => {
      const id = this.discussionId();
      if (id) {
        untracked(() => this.selectDiscussionById(id));
      }
    });
  }

  ngOnInit() {
    this.loadDiscussions();
  }

  private getDiscussionsConnector() {
    const appName = this.applicationStore.name();
    if (appName === UserApplications.OWNER) {
      return this.api.business().etxe().discussions().owner();
    }
    return this.api.business().etxe().discussions().agent();
  }

  loadDiscussions(archived = false) {
    const appName = this.applicationStore.name();
    const user = this.userStore.current();
    if (appName === UserApplications.AGENT && !user?.profiles?.agent) {
      this.discussions.set([]);
      this.isLoadingDiscussions.set(false);
      this.hasMoreDiscussions.set(false);
      return;
    }

    this.isLoadingDiscussions.set(true);
    this.discussionsSkip = 0;
    this.hasMoreDiscussions.set(true);

    this.getDiscussionsConnector()
      .getDiscussions(archived, this.discussionsLimit, this.discussionsSkip)
      .pipe(
        tap((list) => {
          this.discussions.set(list || []);
          if ((list || []).length < this.discussionsLimit) {
            this.hasMoreDiscussions.set(false);
          }

          // Select or update the active discussion if discussionId is set and matches
          const activeId = this.discussionId();
          if (activeId) {
            const found = list?.find((d) => (d._id as unknown as string) === activeId);
            if (found) {
              const currentActive = this.activeDiscussion();
              if (!currentActive || currentActive._id !== found._id) {
                this.selectDiscussion(found);
              } else {
                // Update in-place to refresh participant unreadCount
                this.activeDiscussion.set(found);
              }
            } else {
              // Try loading without limit to find it if not in the first page
              this.selectDiscussionById(activeId);
            }
          }
        }),
        catchError((err) => {
          console.error('Failed to load discussions:', err);
          return of(null);
        }),
        finalize(() => this.isLoadingDiscussions.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadMoreDiscussions() {
    const appName = this.applicationStore.name();
    const user = this.userStore.current();
    if (appName === UserApplications.AGENT && !user?.profiles?.agent) {
      return;
    }

    if (this.isLoadingMoreDiscussions() || !this.hasMoreDiscussions() || this.isLoadingDiscussions()) {
      return;
    }

    this.isLoadingMoreDiscussions.set(true);
    const nextSkip = this.discussionsSkip + this.discussionsLimit;

    this.getDiscussionsConnector()
      .getDiscussions(this.currentTab() === 'archived', this.discussionsLimit, nextSkip)
      .pipe(
        tap((list) => {
          if ((list || []).length > 0) {
            this.discussions.update((current) => [...current, ...list]);
            this.discussionsSkip = nextSkip;
          }
          if ((list || []).length < this.discussionsLimit) {
            this.hasMoreDiscussions.set(false);
          }
        }),
        catchError((err) => {
          console.error('Failed to load more discussions:', err);
          return of(null);
        }),
        finalize(() => this.isLoadingMoreDiscussions.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onFilterTabChanged(tab: 'all' | 'unread' | 'archived') {
    this.currentTab.set(tab);
    this.loadDiscussions(tab === 'archived');
  }

  selectDiscussion(discussion: DiscussionDto) {
    if (!discussion._id) return;
    if (this.activeDiscussion()?._id === discussion._id) {
      return;
    }
    this.activeDiscussion.set(discussion);

    const userId = this.userStore.current()?._id;

    // Locally reset agent/owner unread count for instantaneous UI feedback
    this.discussions.update((list) =>
      list.map((d) => {
        if (d._id === discussion._id) {
          return {
            ...d,
            participants: d.participants.map((p) => {
              if (userId && p.participant.toString() === userId.toString()) {
                return { ...p, unreadCount: 0 };
              }
              return p;
            }),
          };
        }
        return d;
      }),
    );

    this.discussionSelected.emit(discussion._id as unknown as string);
  }

  selectDiscussionById(id: string) {
    // If discussions are already loaded, find and select
    const disc = this.discussions().find((d) => (d._id as unknown as string) === id);
    if (disc) {
      if (this.activeDiscussion()?._id !== disc._id) {
        this.selectDiscussion(disc);
      }
    } else {
      // If we are currently loading discussions, do not trigger a new parallel request
      if (this.isLoadingDiscussions()) {
        return;
      }
      this.isLoadingDiscussions.set(true);
      // Fetch discussions again if not loaded yet
      this.getDiscussionsConnector()
        .getDiscussions(this.currentTab() === 'archived')
        .pipe(
          tap((list) => {
            this.discussions.set(list || []);
            const found = list?.find((d) => (d._id as unknown as string) === id);
            if (found && this.activeDiscussion()?._id !== found._id) {
              this.selectDiscussion(found);
            }
          }),
          catchError((err) => {
            console.error('Failed to load discussions in selectDiscussionById:', err);
            return of(null);
          }),
          finalize(() => this.isLoadingDiscussions.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  readonly isLoadingMore = signal<boolean>(false);
  readonly hasMoreMessages = signal<boolean>(true);

  loadMessages(id: string, showSpinner = true) {
    if (showSpinner) {
      this.isLoadingMessages.set(true);
      this.hasMoreMessages.set(true);
    }
    const limit = showSpinner ? 20 : Math.max(20, this.messages().length);

    this.getDiscussionsConnector()
      .getDiscussionMessages(id, limit, 0)
      .pipe(
        tap((list) => {
          this.messages.set(list || []);
          if (showSpinner) {
            this.scrollToBottom();
            if ((list || []).length < 20) {
              this.hasMoreMessages.set(false);
            }
          }
        }),
        catchError((err) => {
          console.error('Failed to load messages:', err);
          return of(null);
        }),
        finalize(() => this.isLoadingMessages.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadMoreMessages() {
    const active = this.activeDiscussion();
    if (
      !active ||
      !active._id ||
      this.isLoadingMore() ||
      this.isLoadingMessages() ||
      !this.hasMoreMessages()
    ) {
      return;
    }

    this.isLoadingMore.set(true);
    const id = active._id as unknown as string;
    const currentCount = this.messages().length;

    this.getDiscussionsConnector()
      .getDiscussionMessages(id, 20, currentCount)
      .pipe(
        tap((list) => {
          const newMsgs = list || [];
          if (newMsgs.length < 20) {
            this.hasMoreMessages.set(false);
          }
          this.messages.update((existing) => [...newMsgs, ...existing]);
        }),
        catchError((err) => {
          console.error('Failed to load more messages:', err);
          return of(null);
        }),
        finalize(() => this.isLoadingMore.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleIncomingMessageNotification(metadata: {
    discussionId?: string;
    message?: MessageDto;
    type?: string;
    messageId?: string;
  }) {
    const discussionId = metadata.discussionId;
    if (!discussionId) return;

    const active = this.activeDiscussion();
    if (active && active._id.toString() === discussionId.toString()) {
      if (metadata.type === 'chat_message_deleted' && metadata.messageId) {
        this.messages.update((list) => list.filter((m) => m._id.toString() !== metadata.messageId));
        // If it was pinned, clear pinnedMessage locally
        const userId = this.userStore.current()?._id;
        const currentParticipantIndex = active.participants.findIndex(
          (p) => userId && p.participant.toString() === userId.toString(),
        );
        if (
          currentParticipantIndex !== -1 &&
          active.participants[currentParticipantIndex].pinnedMessage?.toString() === metadata.messageId
        ) {
          this.activeDiscussion.update((d) => {
            if (!d) return d;
            const nextParticipants = [...d.participants];
            nextParticipants[currentParticipantIndex] = {
              ...nextParticipants[currentParticipantIndex],
              pinnedMessage: undefined,
            };
            return { ...d, participants: nextParticipants };
          });
        }
      } else if (metadata.message) {
        const msg = metadata.message;
        this.messages.update((list) => {
          if (list.some((m) => m._id === msg._id)) {
            return list;
          }
          return [...list, msg];
        });
        this.scrollToBottom();

        this.getDiscussionsConnector()
          .getDiscussionMessages(discussionId, 1, 0)
          .pipe(
            catchError(() => of(null)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe();

        // Only reload appointment when the incoming message represents a visit action
        const isVisitAction =
          msg.actionType?.startsWith('property_visit') ||
          (msg.type === 'action' && msg.actionType?.includes('visit'));
        if (isVisitAction) {
          this.loadAppointment(discussionId);
          this.loadApplication(discussionId);
        }
      } else {
        this.loadMessages(discussionId, false);
      }
    }

    this.loadDiscussions(this.currentTab() === 'archived');
  }

  private performSendMessage(tempId: string, text: string, replyToId?: string) {
    const active = this.activeDiscussion();
    if (!active || !active._id) return;

    this.getDiscussionsConnector()
      .sendMessage(active._id as unknown as string, text, replyToId)
      .pipe(
        tap((msg: MessageDto) => {
          // Replace temp message with actual message
          this.messages.update((list) =>
            list.map((m) => ((m._id as unknown as string) === tempId ? msg : m)),
          );

          const userId = this.userStore.current()?._id;
          const updateDiscussionInfo = (d: DiscussionDto) => {
            if ((d._id as unknown as string) !== (active._id as unknown as string)) {
              return d;
            }
            return {
              ...d,
              lastMessageText: text,
              lastMessageAt: new Date().toISOString(),
              participants: d.participants.map((p) => {
                if (userId && p.participant.toString() !== userId.toString()) {
                  return { ...p, unreadCount: p.unreadCount + 1 };
                }
                return p;
              }),
            };
          };

          // Update last message in the thread list and increment local unread counts
          this.discussions.update((list) => list.map(updateDiscussionInfo));
          this.activeDiscussion.update((d) => (d ? updateDiscussionInfo(d) : null));

          this.scrollToBottom();
        }),
        catchError((err: { status?: number; error?: { message?: string } }) => {
          console.error('Failed to send message:', err);
          const errorMsg =
            err?.status === 429
              ? 'Trop de messages envoyés. Veuillez patienter avant de réessayer.'
              : err?.error?.message || "Une erreur est survenue lors de l'envoi.";

          // Set temp message status to 'failed' and store errorMessage
          this.messages.update((list) =>
            list.map((m) => {
              if ((m._id as unknown as string) === tempId) {
                return {
                  ...m,
                  status: 'failed',
                  errorMessage: errorMsg,
                } as unknown as MessageDto;
              }
              return m;
            }),
          );
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  sendMessage(event: { text: string; replyToId?: string }) {
    const active = this.activeDiscussion();
    const { text, replyToId } = event;
    if (!text || !active || !active._id) return;

    const currentUserId = this.userStore.current()?._id;
    if (!currentUserId) return;

    const tempId = `temp-${Date.now()}-${crypto.randomUUID()}`;

    // Find the message we are replying to, if any
    let replyTo: MessageDto | undefined = undefined;
    if (replyToId) {
      const parentMsg = this.messages().find((m) => (m._id as unknown as string) === replyToId);
      if (parentMsg) {
        replyTo = parentMsg;
      }
    }

    const tempMsg: MessageDto = {
      _id: tempId as unknown as MessageDto['_id'],
      discussion: active._id as unknown as string,
      sender: currentUserId as unknown as MessageDto['sender'],
      text,
      createdAt: new Date().toISOString(),
      readBy: [currentUserId as unknown as string],
      replyTo,
      status: 'sending',
    } as unknown as MessageDto;

    // Add temp message to list and scroll
    this.messages.update((list) => [...list, tempMsg]);
    this.scrollToBottom();

    this.performSendMessage(tempId, text, replyToId);
  }

  retryMessage(msg: MessageDto) {
    const tempId = msg._id as unknown as string;

    // Set status back to 'sending' and clear errorMessage
    this.messages.update((list) =>
      list.map((m) => {
        if ((m._id as unknown as string) === tempId) {
          return {
            ...m,
            status: 'sending',
            errorMessage: undefined,
          } as unknown as MessageDto;
        }
        return m;
      }),
    );

    const replyToId = msg.replyTo
      ? (msg.replyTo._id as unknown as string) || (msg.replyTo.id as string)
      : undefined;
    this.performSendMessage(tempId, msg.text, replyToId);
  }

  copyMessageText(msg: MessageDto) {
    void navigator.clipboard.writeText(msg.text).then(() => {
      this.alertSnackbar.success({ message: 'Message copié dans le presse-papiers' });
    });
  }

  pinMessage(msg: MessageDto) {
    const active = this.activeDiscussion();
    if (!active || !active._id || !msg._id) return;

    this.getDiscussionsConnector()
      .pinMessage(active._id as unknown as string, msg._id as unknown as string)
      .pipe(
        tap(() => {
          const userId = this.userStore.current()?._id;
          if (!userId) return;

          // Locally update pinnedMessage in activeDiscussion signal
          this.activeDiscussion.update((d) => {
            if (!d) return null;
            return {
              ...d,
              participants: d.participants.map((p) => {
                if (p.participant.toString() === userId.toString()) {
                  return { ...p, pinnedMessage: msg };
                }
                return p;
              }),
            };
          });

          // Also update in discussions list signal
          this.discussions.update((list) =>
            list.map((d) => {
              if (d._id === active._id) {
                return {
                  ...d,
                  participants: d.participants.map((p) => {
                    if (p.participant.toString() === userId.toString()) {
                      return { ...p, pinnedMessage: msg };
                    }
                    return p;
                  }),
                };
              }
              return d;
            }),
          );

          this.alertSnackbar.success({ message: 'Message épinglé' });
        }),
        catchError((err) => {
          console.error('Failed to pin message:', err);
          this.alertSnackbar.error({ message: "Impossible d'épingler le message" });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  unpinActiveMessage() {
    const active = this.activeDiscussion();
    if (!active || !active._id) return;

    this.getDiscussionsConnector()
      .pinMessage(active._id as unknown as string, null)
      .pipe(
        tap(() => {
          const userId = this.userStore.current()?._id;
          if (!userId) return;

          // Locally clear pinnedMessage in activeDiscussion signal
          this.activeDiscussion.update((d) => {
            if (!d) return null;
            return {
              ...d,
              participants: d.participants.map((p) => {
                if (p.participant.toString() === userId.toString()) {
                  return { ...p, pinnedMessage: undefined };
                }
                return p;
              }),
            };
          });

          // Also clear in discussions list signal
          this.discussions.update((list) =>
            list.map((d) => {
              if (d._id === active._id) {
                return {
                  ...d,
                  participants: d.participants.map((p) => {
                    if (p.participant.toString() === userId.toString()) {
                      return { ...p, pinnedMessage: undefined };
                    }
                    return p;
                  }),
                };
              }
              return d;
            }),
          );

          this.alertSnackbar.success({ message: 'Message désépinglé' });
        }),
        catchError((err) => {
          console.error('Failed to unpin message:', err);
          this.alertSnackbar.error({ message: 'Impossible de désépingler le message' });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  infoMessage(msg: MessageDto) {
    const userId = this.userStore.current()?._id;
    const isMe = userId && msg.sender.toString() === userId.toString();
    const senderName = isMe ? 'Vous' : 'Destinataire';
    const dateStr = new Date(msg.createdAt).toLocaleString();
    this.alertSnackbar.info({
      title: 'Informations du message',
      message: `Envoyé par : ${senderName}\nDate : ${dateStr}\nLu par : ${msg.readBy.length} participant(s)`,
    });
  }

  deleteMessage(msg: MessageDto) {
    if (!msg._id || !msg.discussion) return;
    this.getDiscussionsConnector()
      .deleteMessage(msg.discussion, msg._id as unknown as string)
      .pipe(
        tap(() => {
          this.messages.update((list) => list.filter((m) => m._id !== msg._id));
          this.alertSnackbar.success({ message: 'Message supprimé' });
          // If deleted message was pinned, clear it locally as well
          const activeDiscussionValue = this.activeDiscussion();
          if (activeDiscussionValue) {
            const userId = this.userStore.current()?._id;
            const currentParticipantIndex = activeDiscussionValue.participants.findIndex(
              (p) => userId && p.participant.toString() === userId.toString(),
            );
            if (
              currentParticipantIndex !== -1 &&
              activeDiscussionValue.participants[currentParticipantIndex].pinnedMessage?.toString() ===
                msg._id.toString()
            ) {
              this.activeDiscussion.update((d) => {
                if (!d) return d;
                const nextParticipants = [...d.participants];
                nextParticipants[currentParticipantIndex] = {
                  ...nextParticipants[currentParticipantIndex],
                  pinnedMessage: undefined,
                };
                return { ...d, participants: nextParticipants };
              });
            }
          }
          // Also reload discussions list to update last message preview
          this.loadDiscussions(this.currentTab() === 'archived');
        }),
        catchError((err) => {
          console.error('Failed to delete message:', err);
          this.alertSnackbar.error({ message: 'Impossible de supprimer le message' });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  markAsUnread(discussion: DiscussionDto, event: Event) {
    event.stopPropagation();
    if (!discussion._id) return;
    const userId = this.userStore.current()?._id;
    this.getDiscussionsConnector()
      .markAsUnread(discussion._id as unknown as string)
      .pipe(
        tap(() => {
          this.discussions.update((list) =>
            list.map((d) => {
              if (d._id === discussion._id) {
                return {
                  ...d,
                  participants: d.participants.map((p) => {
                    if (userId && p.participant.toString() === userId.toString()) {
                      return { ...p, unreadCount: 1 };
                    }
                    return p;
                  }),
                };
              }
              return d;
            }),
          );
        }),
        catchError((err) => {
          console.error('Failed to mark as unread:', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  toggleStar(discussion: DiscussionDto, event: Event) {
    event.stopPropagation();
    if (!discussion._id) return;
    const userId = this.userStore.current()?._id;
    const isCurrentlyStarred = this.isDiscussionStarred(discussion);
    const nextStarred = !isCurrentlyStarred;

    this.getDiscussionsConnector()
      .setStarred(discussion._id as unknown as string, nextStarred)
      .pipe(
        tap(() => {
          const mapper = (d: DiscussionDto): DiscussionDto => {
            if (d._id === discussion._id) {
              return {
                ...d,
                participants: d.participants.map((p) => {
                  if (userId && p.participant.toString() === userId.toString()) {
                    return { ...p, isStarred: nextStarred };
                  }
                  return p;
                }),
              };
            }
            return d;
          };

          this.discussions.update((list) => list.map(mapper));
          const active = this.activeDiscussion();
          if (active && active._id === discussion._id) {
            this.activeDiscussion.update((d) => (d ? mapper(d) : null));
          }
        }),
        catchError((err) => {
          console.error('Failed to toggle star:', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  isDiscussionArchived(discussion: DiscussionDto): boolean {
    const userId = this.userStore.current()?._id;
    const me = discussion.participants.find((p) => userId && p.participant.toString() === userId.toString());
    return !!me?.isArchived;
  }

  archiveDiscussion(discussion: DiscussionDto, event: Event) {
    event.stopPropagation();
    if (!discussion._id) return;
    const isCurrentlyArchived = this.isDiscussionArchived(discussion);
    this.getDiscussionsConnector()
      .setArchived(discussion._id as unknown as string, !isCurrentlyArchived)
      .pipe(
        tap(() => {
          this.discussions.update((list) => list.filter((d) => d._id !== discussion._id));
          const active = this.activeDiscussion();
          if (active && active._id === discussion._id) {
            this.activeDiscussion.set(null);
          }
        }),
        catchError((err) => {
          console.error('Failed to archive discussion:', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private isDiscussionStarred(discussion: DiscussionDto): boolean {
    const userId = this.userStore.current()?._id;
    const me = discussion.participants.find((p) => userId && p.participant.toString() === userId.toString());
    return !!me?.isStarred;
  }

  markActiveDiscussionAsRead() {
    const active = this.activeDiscussion();

    if (!active || !active._id) return;

    const userId = this.userStore.current()?._id;
    if (!userId) return;

    // Check in discussions list for the actual current unread count
    const discInList = this.discussions().find((d) => d._id === active._id);
    if (!discInList) return;

    const myParticipant = discInList.participants.find((p) => p.participant.toString() === userId.toString());
    if (!myParticipant || myParticipant.unreadCount === 0) {
      return;
    }

    // 1. Instantly reset client-side unreadCount for this discussion in list to 0
    this.discussions.update((list) =>
      list.map((d) => {
        if (d._id === active._id) {
          return {
            ...d,
            participants: d.participants.map((p) => {
              if (p.participant.toString() === userId.toString()) {
                return { ...p, unreadCount: 0 };
              }
              return p;
            }),
          };
        }
        return d;
      }),
    );

    // Also update activeDiscussion signal
    this.activeDiscussion.update((d) => {
      if (!d) return null;
      return {
        ...d,
        participants: d.participants.map((p) => {
          if (p.participant.toString() === userId.toString()) {
            return { ...p, unreadCount: 0 };
          }
          return p;
        }),
      };
    });

    // 2. Call backend to reset the unread count in DB!
    this.getDiscussionsConnector()
      .getDiscussionMessages(active._id as unknown as string, 1, 0)
      .pipe(
        catchError((err) => {
          console.error('Failed to reset unread count on backend:', err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private scrollToBottom() {
    if (this.chatFeed) {
      this.chatFeed.scrollToBottom();
    }
  }

  handleChatAction(action: ChatPendingAction): void {
    if (action.type === 'property_visit') {
      this.openVisitModal();
    }
  }

  openVisitModal(): void {
    const activeId = this.activeDiscussion()?._id?.toString();
    if (!activeId) {
      this.launchVisitModalWithAppointment(this.currentAppointment());
      return;
    }

    const isOwner = this.isOwner();
    const getAppt$ = isOwner
      ? this.api.business().etxe().discussions().owner().getAppointment(activeId)
      : this.api.business().etxe().discussions().agent().getAppointment(activeId);

    getAppt$
      .pipe(
        tap((freshAppt) => {
          if (freshAppt) {
            this.currentAppointment.set(freshAppt);
          }
          this.launchVisitModalWithAppointment(freshAppt || this.currentAppointment());
        }),
        catchError(() => {
          this.launchVisitModalWithAppointment(this.currentAppointment());
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private launchVisitModalWithAppointment(appt: AppointmentDto | null): void {
    const dialogRef = this.dialog.open<
      DiscussionVisitDialogComponent,
      DiscussionVisitDialogData,
      DiscussionVisitDialogResult
    >(DiscussionVisitDialogComponent, {
      width: '100%',
      maxWidth: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        appointment: appt,
        application: this.currentApplication(),
        isOwner: this.isOwner(),
        property: this.propertyContext(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is DiscussionVisitDialogResult => Boolean(result)),
        tap((result) => {
          switch (result.action) {
            case 'proposeVisit':
              this.openVisitInvitationDialog();
              break;
            case 'confirmSlot':
              this.confirmVisitSlot(result.slotId);
              break;
            case 'requestReschedule':
              this.requestRescheduleVisit(result.reason);
              break;
            case 'markAsVisited':
              this.markVisitAsCompleted();
              break;
            case 'reportNoShow':
              this.reportVisitNoShow(result.reason);
              break;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openVisitInvitationDialog(): void {
    const prop = this.propertyContext();
    const active = this.activeDiscussion();
    if (!prop || !active) return;

    const currentUserId = this.userStore.current()?._id;
    const other = active.participants.find((p) => p.participant.toString() !== currentUserId?.toString());
    const isAgent = other?.participantType === 'agent';
    const agentName = other?.firstName || 'l’agent immobilier';

    if (isAgent && other?.participant) {
      this.api
        .business()
        .etxe()
        .public()
        .agents()
        .get(other.participant as unknown as AgentProfileId)
        .pipe(
          tap((profile) => {
            this.launchVisitDialog(prop, agentName, profile?.work?.openingHours || null);
          }),
          catchError(() => {
            this.launchVisitDialog(prop, agentName, null);
            return of(null);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.launchVisitDialog(prop, agentName, null);
    }
  }

  private launchVisitDialog(
    prop: PropertyDto,
    agentName: string,
    openingHours: OpeningHoursDto | null,
  ): void {
    const currentAppt = this.currentAppointment();
    const excludedSlots = (currentAppt?.proposedSlots || []).filter(
      (s) => s.status === AppointmentSlotStatus.DECLINED,
    );

    const dialogRef = this.dialog.open<
      PropertyVisitInvitationDialogComponent,
      PropertyVisitInvitationDialogData,
      VisitInvitationResult
    >(PropertyVisitInvitationDialogComponent, {
      width: '100%',
      maxWidth: '680px',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        property: prop,
        agentName,
        openingHours,
        excludedSlots,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is VisitInvitationResult =>
          Boolean(result && result.proposedSlots && result.proposedSlots.length > 0),
        ),
        switchMap((result) => {
          const activeId = this.activeDiscussion()?._id?.toString();
          if (!activeId) return of(null);

          return this.api
            .business()
            .etxe()
            .discussions()
            .owner()
            .proposeAppointment(activeId, result.proposedSlots, result.notes)
            .pipe(
              tap((createdAppt) => {
                this.currentAppointment.set(createdAppt);
                this.loadApplication(activeId);
                this.loadMessages(activeId);
                this.alertSnackbar.success({
                  message: 'Votre proposition de visite avec 3 créneaux a été envoyée.',
                });
              }),
              catchError((err) => {
                this.alertSnackbar.error({
                  message: err?.error?.message || 'Erreur lors de l’envoi de la proposition.',
                });
                return of(null);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadAppointment(discussionId: string): void {
    const isOwner = this.isOwner();
    const getAppt$ = isOwner
      ? this.api.business().etxe().discussions().owner().getAppointment(discussionId)
      : this.api.business().etxe().discussions().agent().getAppointment(discussionId);

    getAppt$
      .pipe(
        tap((appt) => this.currentAppointment.set(appt)),
        catchError(() => {
          this.currentAppointment.set(null);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  loadApplication(discussionId: string): void {
    const isOwner = this.isOwner();
    const getApp$ = isOwner
      ? this.api.business().etxe().discussions().owner().getApplication(discussionId)
      : this.api.business().etxe().discussions().agent().getApplication(discussionId);

    getApp$
      .pipe(
        tap((app) => this.currentApplication.set(app)),
        catchError(() => {
          this.currentApplication.set(null);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  confirmVisitSlot(slotId: string): void {
    const activeId = this.activeDiscussion()?._id?.toString();
    if (!activeId) return;

    this.api
      .business()
      .etxe()
      .discussions()
      .agent()
      .confirmAppointment(activeId, slotId)
      .pipe(
        tap((updatedAppt) => {
          this.currentAppointment.set(updatedAppt);
          this.loadApplication(activeId);
          this.loadMessages(activeId);
          this.alertSnackbar.success({
            message: 'Le créneau de visite a été confirmé.',
          });
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || 'Erreur lors de la confirmation du créneau.',
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  requestRescheduleVisit(reason?: string): void {
    const activeId = this.activeDiscussion()?._id?.toString();
    if (!activeId) return;

    this.api
      .business()
      .etxe()
      .discussions()
      .agent()
      .requestRescheduleAppointment(activeId, reason)
      .pipe(
        tap((updatedAppt) => {
          this.currentAppointment.set(updatedAppt);
          this.loadApplication(activeId);
          this.loadMessages(activeId);
          this.alertSnackbar.success({
            message: "Votre demande d'autres créneaux a été envoyée au propriétaire.",
          });
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || "Erreur lors de l'envoi de la demande.",
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  markVisitAsCompleted(): void {
    const activeId = this.activeDiscussion()?._id?.toString();
    if (!activeId) return;

    this.api
      .business()
      .etxe()
      .discussions()
      .agent()
      .completeAppointment(activeId)
      .pipe(
        tap((updatedAppt) => {
          this.currentAppointment.set(updatedAppt);
          this.loadApplication(activeId);
          this.loadMessages(activeId);
          this.alertSnackbar.success({
            message: 'La visite est marquée comme réalisée.',
          });
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || 'Erreur lors de la validation de la visite.',
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  reportVisitNoShow(reason?: string): void {
    const activeId = this.activeDiscussion()?._id?.toString();
    if (!activeId) return;

    this.api
      .business()
      .etxe()
      .discussions()
      .owner()
      .reportNoShow(activeId, reason)
      .pipe(
        tap((updatedAppt) => {
          this.currentAppointment.set(updatedAppt);
          this.loadApplication(activeId);
          this.loadMessages(activeId);
          this.alertSnackbar.warn({
            message: 'L’absence de l’agent a été enregistrée. La candidature est clôturée.',
          });
        }),
        catchError((err) => {
          this.alertSnackbar.error({
            message: err?.error?.message || 'Erreur lors du signalement de l’absence.',
          });
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openFinalOfferDialog(): void {
    const prop = this.propertyContext();
    if (!prop || !prop._id) return;

    if (!this.isOwner()) {
      void this.router.navigate(['/follow-up', prop._id, 'final-offer']);
      return;
    }

    const currentApp = this.currentApplication();
    const dialogRef = this.dialog.open<
      PropertyApplicationDialogComponent,
      PropertyApplicationDialogData,
      PropertyApplicationCreationDto
    >(PropertyApplicationDialogComponent, {
      width: '100%',
      maxWidth: '720px',
      data: {
        property: prop,
        activeStep: 3,
        initialDto: {
          propertyId: prop._id,
          engagement: currentApp?.engagement || {
            commissionRate: prop.mandate?.targetCommissionRate ?? 3.5,
            services: [],
          },
          message: '',
        },
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => Boolean(result)),
        tap(() => {
          this.alertSnackbar.success({
            message: 'Votre avis de valeur et offre finale ont été enregistrés avec succès.',
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}

function getFormattedDateHeader(dateInput: Date | string, lang = 'fr-FR'): string {
  const date = new Date(dateInput);
  const now = new Date();

  const dateZero = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowZero = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffTime = nowZero.getTime() - dateZero.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return lang.startsWith('fr') ? "Aujourd'hui" : 'Today';
  } else if (diffDays === 1) {
    return lang.startsWith('fr') ? 'Hier' : 'Yesterday';
  } else {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    };
    return new Intl.DateTimeFormat(lang, options).format(date);
  }
}
